import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_700Bold } from '@expo-google-fonts/inter';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import { useLanguage } from '@/contexts/LanguageContext';

interface Category {
  id: string;
  label: string;
}

export default function DiarioSentimentoCategoriaScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  
  const categories: Category[] = [
    { id: 'vida-pessoal', label: t('diary.categories.vida-pessoal') },
    { id: 'trabalho', label: t('diary.categories.trabalho') },
    { id: 'relacionamentos', label: t('diary.categories.relacionamentos') },
    { id: 'saude', label: t('diary.categories.saude') },
    { id: 'financas', label: t('diary.categories.financas') },
    { id: 'sobriedade', label: t('diary.categories.sobriedade') },
    { id: 'lazer', label: t('diary.categories.lazer') },
    { id: 'espiritualidade', label: t('diary.categories.espiritualidade') },
    { id: 'educacao', label: t('diary.categories.educacao') },
    { id: 'outros', label: t('diary.categories.outros') },
  ];
  const { emotion, selectedDate } = useLocalSearchParams<{ emotion: string; selectedDate?: string }>();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_700Bold,
    'Sentient-Regular': require('../assets/fonts/Sentient-Regular.otf'),
  });

  if (!fontsLoaded) {
    return null;
  }

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleContinue = () => {
    if (selectedCategories.length > 0) {
      router.push({
        pathname: '/diario-sentimento-nota',
        params: { 
          emotion: emotion as string,
          categories: selectedCategories.join(','),
          selectedDate: selectedDate || new Date().toISOString().split('T')[0],
          returnTo: router.canGoBack() ? 'back' : 'menu'
        }
      });
    }
  };

  const handleBack = () => {
    router.back();
  };

  const isSelected = (categoryId: string) => selectedCategories.includes(categoryId);
  const canContinue = selectedCategories.length > 0;

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>{t('diary.sentiment.title')}</Text>
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path d="M16.5859 19L9.58594 12L16.5859 5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
            </Svg>
          </TouchableOpacity>
        </View>
      </View>

      {/* Body */}
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.body}>
        <Text style={styles.questionText}>{t('diary.sentiment.context')}</Text>

        {/* Categories */}
        <View style={styles.categoriesContainer}>
          {categories.map((category) => (
            <TouchableOpacity
              key={category.id}
              style={[
                styles.categoryCard,
                isSelected(category.id) && styles.categoryCardSelected
              ]}
              onPress={() => handleCategoryToggle(category.id)}
              activeOpacity={0.7}
            >
              <Text style={[
                styles.categoryText,
                isSelected(category.id) && styles.categoryTextSelected
              ]}>
                {category.label}
              </Text>
              <View style={[
                styles.checkbox,
                isSelected(category.id) && styles.checkboxSelected
              ]}>
                {isSelected(category.id) && (
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M12.6654 4.7915L6.2487 11.2082L3.33203 8.2915" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                )}
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {/* Button Section */}
      <View style={styles.buttonSection}>
        <PrimaryButton
          title={t('diary.sentiment.nextButton')}
          onPress={handleContinue}
          disabled={!canContinue}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E6EBF0',
    height: 56,
    justifyContent: 'center',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 14,
  },
  backButton: {
    position: 'absolute',
    left: 0,
    padding: 4,
  },
  scrollView: {
    flex: 1,
  },
  body: {
    padding: 24,
    gap: 24,
  },
  questionText: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
  },
  categoriesContainer: {
    gap: 12,
  },
  categoryCard: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    height: 64,
  },
  categoryCardSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  categoryText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    flex: 1,
    lineHeight: 19.6,
  },
  categoryTextSelected: {
    color: '#151515',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#015480',
    padding: 4,
  },
  buttonSection: {
    padding: 24,
    backgroundColor: '#FFFFFF',
  },
});